import React, { Fragment, useRef, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { twClassNames } from "../../../helpers/twClassNames";
import { Chip } from "../../Chip";

type Tier = {
    credits: number;
    monthlyPrice: number;
    annualPrice: number;
    monthlyDiscountPercent: number;
    annualDiscountPercent: number;
    isDefault?: boolean;
};

type CreditTierSelectorProps = {
    tiers: Tier[];
    selectedTier: Tier;
    onSelect: (tier: Tier) => void;
    isAnnual: boolean;
    baseTier: Tier;
    compact?: boolean;
    currentSubscribedBundleIndex?: number | null;
};

function getPrice(tier: Tier, isAnnual: boolean): number {
    return isAnnual ? tier.annualPrice : tier.monthlyPrice;
}

function getDiscount(tier: Tier, isAnnual: boolean): number {
    return isAnnual ? tier.annualDiscountPercent : tier.monthlyDiscountPercent;
}

function getIncrementalCost(tier: Tier, baseTier: Tier, isAnnual: boolean): number | null {
    const tierPrice = getPrice(tier, isAnnual);
    const basePrice = getPrice(baseTier, isAnnual);
    const diff = tierPrice - basePrice;
    return diff > 0 ? diff : null;
}

function getIntervalCredits(tier: Tier, isAnnual: boolean): number {
    return isAnnual ? tier.credits * 12 : tier.credits;
}

function getIntervalDisplayText(isAnnual: boolean): string {
    return isAnnual ? "year" : "month";
}

export function CreditTierSelector({
    tiers,
    selectedTier,
    onSelect,
    isAnnual,
    baseTier,
    compact = false,
    currentSubscribedBundleIndex = null
}: CreditTierSelectorProps) {
    const discount = getDiscount(selectedTier, isAnnual);
    const [pendingTier, setPendingTier] = useState<Tier | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const handleSelect = (tier: Tier) => {
        setPendingTier(tier);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            onSelect(tier);
            setPendingTier(null);
        }, 150);
    };

    const intervalDisplayText = getIntervalDisplayText(isAnnual);

    return (
        <Listbox value={pendingTier ?? selectedTier} onChange={handleSelect}>
            {({ open }) => (
                <div className="relative">
                    <Listbox.Button className="btn2 xlarge secondary !h-auto w-full !border-gray-300 py-2.5 hover:!border-gray-400 hover:bg-gray-50">
                        <div className="flex w-full items-center justify-between gap-2 text-sm">
                            <span className="font-medium text-gray-900">
                                {getIntervalCredits(selectedTier, isAnnual)} credits{" "}
                                <span className="font-normal text-gray-500">
                                    / {intervalDisplayText}
                                </span>
                            </span>
                            <Chip
                                color="yellow"
                                className={discount > 0 && !open ? "" : "invisible"}
                            >
                                Save {discount || 0}% per credit
                            </Chip>
                        </div>
                        <i className="far fa-chevron-down text-xs text-gray-400" />
                    </Listbox.Button>

                    <Transition
                        as={Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Listbox.Options className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white p-1 shadow-lg">
                            {tiers.map((tier, tierIndex) => {
                                const tierDiscount = getDiscount(tier, isAnnual);
                                const incrementalCost = getIncrementalCost(
                                    tier,
                                    baseTier,
                                    isAnnual
                                );
                                const intervalCredits = getIntervalCredits(tier, isAnnual);
                                const isCurrentTier =
                                    currentSubscribedBundleIndex !== null &&
                                    currentSubscribedBundleIndex === tierIndex;
                                const isTierLessThanCurrentTier =
                                    tierIndex < (currentSubscribedBundleIndex || 0);
                                const shouldShowIncrementalCost =
                                    !isCurrentTier && !isTierLessThanCurrentTier;

                                return (
                                    <Listbox.Option
                                        key={intervalCredits}
                                        value={tier}
                                        as={Fragment}
                                    >
                                        {({ selected }) => (
                                            <li
                                                className={twClassNames(
                                                    "flex cursor-pointer rounded-lg px-3 py-2.5 text-sm transition duration-200",
                                                    compact
                                                        ? "flex-col gap-1"
                                                        : "items-center justify-between",
                                                    selected
                                                        ? "border border-upgrade bg-yellow-50"
                                                        : "hover:bg-gray-50"
                                                )}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={twClassNames(
                                                            "font-medium",
                                                            !compact && "w-[132px]",
                                                            selected
                                                                ? "text-yellow-800"
                                                                : "text-primary"
                                                        )}
                                                    >
                                                        {intervalCredits} credits{" "}
                                                        <span
                                                            className={twClassNames(
                                                                "font-normal",
                                                                selected
                                                                    ? "text-yellow-800"
                                                                    : "text-tertiary"
                                                            )}
                                                        >
                                                            / {intervalDisplayText}
                                                        </span>
                                                    </span>
                                                    {shouldShowIncrementalCost &&
                                                        !compact &&
                                                        incrementalCost != null && (
                                                            <Chip
                                                                color={selected ? "yellow" : "gray"}
                                                            >
                                                                +${incrementalCost}/mo
                                                            </Chip>
                                                        )}
                                                    {isCurrentTier && (
                                                        <Chip color="purple">Current bundle</Chip>
                                                    )}
                                                </div>
                                                <div
                                                    className={twClassNames(
                                                        "flex items-center gap-1",
                                                        compact && "flex-wrap"
                                                    )}
                                                >
                                                    {shouldShowIncrementalCost &&
                                                        compact &&
                                                        incrementalCost != null && (
                                                            <Chip color="gray">
                                                                +${incrementalCost}
                                                                {isAnnual ? "/mo" : ""}
                                                            </Chip>
                                                        )}
                                                    {tierDiscount > 0 && (
                                                        <Chip color="yellow">
                                                            Save {tierDiscount}% per credit
                                                        </Chip>
                                                    )}
                                                </div>
                                            </li>
                                        )}
                                    </Listbox.Option>
                                );
                            })}
                        </Listbox.Options>
                    </Transition>
                </div>
            )}
        </Listbox>
    );
}
